import { useEffect, useMemo, useState } from "react";
import {
  applyCatalogProduct,
  applyReleaseEvidence,
  buildSecureMacProStorefront,
} from "@/config/secureMacProStorefront";
import {
  fetchConsumerCatalog,
  pickSecureMacProCatalogProduct,
} from "@/lib/consumerCommerce";

/**
 * Hydrate the Pro storefront from consumer-platform catalog.
 * Release evidence for public marketing claims is not loaded from an
 * unauthenticated endpoint; pages stay fail-closed for notarization until a
 * verified evidence object is supplied by a future release-evidence build input.
 */
export function useSecureMacProStorefront(releaseEvidence = null) {
  const [catalogProduct, setCatalogProduct] = useState(null);
  const [catalogReady, setCatalogReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalog = await fetchConsumerCatalog();
        if (cancelled) return;
        setCatalogProduct(pickSecureMacProCatalogProduct(catalog));
        setCatalogReady(Boolean(catalog?.ready));
        setError("");
      } catch (err) {
        if (cancelled) return;
        setCatalogProduct(null);
        setCatalogReady(false);
        setError(err?.message || "Catalog unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const storefront = useMemo(() => {
    let next = buildSecureMacProStorefront();
    next = applyCatalogProduct(next, catalogProduct);
    next = applyReleaseEvidence(next, releaseEvidence);
    return next;
  }, [catalogProduct, releaseEvidence]);

  return { storefront, catalogReady, catalogProduct, error };
}
